import {  useEffect, useState } from "react";
import { Alert, Grid, Button, Progress, LoadingOverlay, Modal, Center, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { emailStepStore } from "@entities/StepEmail/store/emailStepStore";
import { appStore } from "@/app/store/appStore";
import { config, isDevelopmentServer } from "@/api/OpenApiClient";
import { closeApp } from "@/shared/utils/helpers";

export type UpdateComponentProps = unknown;

export const VERSIONS = {
	HardUpdateVersion: 200,
	SoftUpdateVersion: 354,
	CurrentVersion: 354,
	VERSION_WITH_SENTRY_SUPPORT: 158,
	VERSION_WITH_EXTERNAL_BROWSER_SUPPORT: 296,
	NEW_SUB_PROCESS_START: 300,
	SHA_256_HEAD_AND_TAIL: 354
};

const UpdateComponent: React.FC<UpdateComponentProps> = () => {
	const { t } = useTranslation();

	const [loading, setLoading] = useState(false);
	const [loadingProgress, setLoadingProgress] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [softUpdateVersion, setSoftUpdateVersion] = useState(VERSIONS.SoftUpdateVersion);
	const [hardUpdateVersion, setHardUpdateVersion] = useState(VERSIONS.HardUpdateVersion);

	const appCode = appStore((state) => state.appCode);
	const updateRequired = appStore((state) => state.updateRequired);
	const gameConfig = appStore((state) => state.gameConfig);
	const appVersion = appStore((state) => state.appVersion);
	const runningInLauncher = appStore((state) => state.runningInLauncher);


	const icon = <IconInfoCircle />;

	async function handleUpdateClick() {
		setLoading(true);

		try {
			const url =
				gameConfig.PublicWebClientConfig.Launcher?.Update?.DownloadUrl ||
				`https://p.fxtools.gl/d/api.storage.fxtools.gl/fxid/fxlauncher/msi/${VERSIONS.CurrentVersion}/${appCode === "worldwar3" ? "ww3" : appCode}_${isDevelopmentServer() ? "dev" : "prod"}/setup.msi`;

			console.log(`Downloading update: ${url}`);

			const downloadId = await FxApi.Download.addDownload(url, ["TMP", `setup_${appCode}.msi`]);
			while (true) {
				const downloadStatus = await FxApi.Download.getDownloadState(downloadId);
				if (downloadStatus.finished) {
					break;
				}

				console.log(downloadStatus);
				setLoadingProgress((downloadStatus.downloaded * 100) / downloadStatus.total);
			}

			const path = `${(await FxApi.File.listRoots()).TMP}setup_${appCode}.msi`;
			const file = await FxApi.File.getFileInfo(["TMP", `setup_${appCode}.msi`]);
			console.log(file);
			if (file && file.exist && file.size > 10000) {
				console.log(path);
				const res = await FxApi.shellExecute(path, "");
				if(res.success)
				{
					closeApp();
					setError(t("failed_to_download_launcher_update_file"));
				}
			} else {
				// Failed to download file from server. Please try again later
				setError(t("failed_to_download_launcher_update_file"));
			}
		} catch (e) {
			console.log(e);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (gameConfig && gameConfig.PublicWebClientConfig.Launcher?.Update?.SoftUpdateVersion) {
			setSoftUpdateVersion(gameConfig.PublicWebClientConfig.Launcher.Update.SoftUpdateVersion);
		}
		if (gameConfig && gameConfig.PublicWebClientConfig.Launcher?.Update?.HardUpdateVersion) {
			setHardUpdateVersion(gameConfig.PublicWebClientConfig.Launcher.Update.HardUpdateVersion);
		}
	}, [gameConfig]);



	const isExternalLogin = emailStepStore((state) => state.isExternalLogin);
	useEffect(() => {
		if (!isExternalLogin) {
			appStore.getState().setUpdateRequired(appVersion < softUpdateVersion);
		}
		else
		{
			appStore.getState().setUpdateRequired(false);
		}
	}, [appVersion, isExternalLogin, softUpdateVersion]);
	if (!runningInLauncher) return null;
	// не обновляем если залогинились во внешнем приложении
	if (isExternalLogin) return null;

	if (appVersion < hardUpdateVersion) {
		return (
			<Modal
				centered
				closeOnClickOutside={false}
				withCloseButton={false}
				onClose={
					() => setError(t("this-update-is-required"))
				}
				opened
			>
				<LoadingOverlay visible={loading} />
				<Text m="xl">
					{error && <Text c="red">{error}</Text>}

					{/*Please update launcher. You are running version <b>{appVersion}</b>, we recommend{' '}*/}
					{/*<b>{recommendedVersion}</b>*/}
					{t("common.LauncherUpdateHint","Great news! We have a new version {{recommendedVersion}} available", { appVersion, recommendedVersion: softUpdateVersion })}
					<Progress radius="xl" m="xl" size="xl" value={loadingProgress} animated />
				</Text>
				<Center m={"xl"}>
					<Button onClick={handleUpdateClick}>{t("download")}</Button>
				</Center>
			</Modal>
		);
	}

	return (
		runningInLauncher &&
		updateRequired && (
			<Alert
				title={t("launcher-update-required")}
				icon={icon}
				color={error ? "red" : "green"}
				variant="filled"
				w="100%"
				radius={0}
				withCloseButton
				onClose={() =>setSoftUpdateVersion(appVersion)}
			>
				<LoadingOverlay visible={loading} />
				<Grid w="600" align="center">
					<Grid.Col span={8}>
						{error && <h2>{error}</h2>}

						{t("common.LauncherUpdateHint","Great news! We have a new version {{recommendedVersion}} available", { appVersion, recommendedVersion: softUpdateVersion })}
						{loading && <Progress radius="xl" size="xl" value={loadingProgress} animated />}
					</Grid.Col>
					<Grid.Col  span={4} >
						<Button onClick={handleUpdateClick}>{t("download")}</Button>
					</Grid.Col>
				</Grid>
			</Alert>
		)
	);
};

export default UpdateComponent;
